import { useEffect, useMemo, useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";

import { FormPicker } from "../workflows/components/FormPicker";
import {
  formTypesOf,
  newCopyLabel,
  type CaseFormRef
} from "../workflows/forms";
import type { FormOption } from "../workflows/types";
import { WorkspaceTreeAPI } from "./api/workspaceTreeApi";

type Props = {
  individualId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The case's forms, so a pick of one it has offers another copy. */
  caseForms: readonly CaseFormRef[];
  /** Creates the copy; resolves once it is in the tree. */
  onAdd: (form: FormOption, label: string | null) => Promise<void>;
};

/**
 * Work Products' "Add Form…": the Fill Forms picker. A form the case doesn't
 * have is added at once; one it has asks for a label for the new copy.
 */
export const AddFormDialog: FC<Props> = ({
  individualId,
  open,
  onOpenChange,
  caseForms,
  onAdd
}) => {
  const [catalog, setCatalog] = useState<FormOption[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [copyOf, setCopyOf] = useState<FormOption | null>(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    if (open && !catalog)
      WorkspaceTreeAPI.getFormTypes(individualId)
        .then((r) => live && setCatalog(formTypesOf(r).catalog))
        .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [open, catalog, individualId]);

  useEffect(() => {
    if (open) return;
    setCopyOf(null);
    setNote("");
    setSaving(false);
    setFailed(false);
  }, [open]);

  const counts = useMemo(() => {
    const out = new Map<string, number>();
    for (const f of caseForms)
      out.set(f.formNumber, (out.get(f.formNumber) ?? 0) + 1);
    return out;
  }, [caseForms]);

  const add = async (form: FormOption, label: string | null) => {
    setSaving(true);
    try {
      await onAdd(form, label);
    } finally {
      setSaving(false);
    }
    onOpenChange(false);
  };

  const pick = (form: FormOption) => {
    if (counts.get(form.formNumber)) {
      setCopyOf(form);
      return;
    }
    void add(form, null);
  };

  const copies = copyOf ? counts.get(copyOf.formNumber) ?? 0 : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="grid-cols-1 gap-0 p-0 sm:max-w-96"
        data-testid="add-form-dialog"
      >
        <DialogHeader className="gap-1 px-4 pt-4 pb-2">
          <DialogTitle className="text-md">
            {copyOf ? `Add Another ${copyOf.formNumber}` : "Add Form"}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {copyOf
              ? `The case has ${copies === 1 ? "a" : copies} ${
                  copyOf.formNumber
                }${
                  copies === 1 ? "" : "s"
                }. A new copy is its own form; a label tells them apart.`
              : "Adds an unfilled form to the case."}
          </DialogDescription>
        </DialogHeader>
        {copyOf ? (
          <>
            <div className="px-4 pb-3">
              <Input
                autoFocus
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !saving)
                    void add(copyOf, newCopyLabel(caseForms, copyOf, note));
                }}
                placeholder="Label, e.g. Beneficiary"
                aria-label={`${copyOf.formNumber} Label`}
                maxLength={40}
              />
            </div>
            <DialogFooter className="mx-0 mb-0 px-4 py-3">
              <Button
                variant="ghost"
                disabled={saving}
                onClick={() => setCopyOf(null)}
              >
                Back
              </Button>
              <Button
                variant="primary"
                disabled={saving}
                onClick={() =>
                  void add(copyOf, newCopyLabel(caseForms, copyOf, note))
                }
              >
                {saving ? <Spinner className="size-3.5" /> : null}
                Add Copy
              </Button>
            </DialogFooter>
          </>
        ) : catalog ? (
          <div className="relative px-2 pb-2">
            <FormPicker
              catalog={catalog}
              counts={counts}
              countLabel={(n) => `${n} on Case`}
              onPick={(f) => !saving && pick(f)}
              testIdPrefix="add-form-option"
            />
            {saving ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <Spinner className="size-4" />
              </div>
            ) : null}
          </div>
        ) : failed ? (
          <p className="px-4 pb-4 text-sm text-fg-3">
            Couldn't load the forms. Try again.
          </p>
        ) : (
          <div
            className="flex flex-col gap-1.5 px-4 pb-4"
            aria-label="Loading forms"
          >
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
